// The ADIF submodes (ADIF 3.1.6), mapping each submode to its parent mode.
// Tokens listed here (FT4, JS8, PSK31, USB, ...) are not valid values for an
// exported MODE field -- the spec requires MODE=<parent> plus SUBMODE=<token>.
// HAMRS stores these as bare mode strings internally (that's what the UI
// shows and what WSJT-X sends over UDP), so the ADIF transformers use this
// table to split them into a compliant MODE/SUBMODE pair at export time.
//
// Sourced from the union of the spec's "Submode Enumeration" table and the
// Submodes column of its "Mode Enumeration" table: the Submode table alone
// is incomplete (it omits several PSK submodes, e.g. PSK125R and PSK500C2,
// that the Mode table lists). The two tables agree on every shared entry.
export const ADIF_SUBMODE_TO_MODE: Readonly<Record<string, string>> = Object.freeze({
  "8PSK1000": "PSK",
  "8PSK1000F": "PSK",
  "8PSK1200F": "PSK",
  "8PSK125": "PSK",
  "8PSK125F": "PSK",
  "8PSK125FL": "PSK",
  "8PSK250": "PSK",
  "8PSK250F": "PSK",
  "8PSK250FL": "PSK",
  "8PSK500": "PSK",
  "8PSK500F": "PSK",
  "AMTORFEC": "TOR",
  "ASCI": "RTTY",
  "C4FM": "DIGITALVOICE",
  "CHIP128": "CHIP",
  "CHIP64": "CHIP",
  "DMR": "DIGITALVOICE",
  "DOM-M": "DOMINO",
  "DOM11": "DOMINO",
  "DOM16": "DOMINO",
  "DOM22": "DOMINO",
  "DOM4": "DOMINO",
  "DOM44": "DOMINO",
  "DOM5": "DOMINO",
  "DOM8": "DOMINO",
  "DOM88": "DOMINO",
  "DOMINOEX": "DOMINO",
  "DOMINOF": "DOMINO",
  "DSTAR": "DIGITALVOICE",
  "FMHELL": "HELL",
  "FREEDV": "DIGITALVOICE",
  "FSK31": "PSK",
  "FSKH105": "HELL",
  "FSKH245": "HELL",
  "FSKHELL": "HELL",
  "FSQCALL": "MFSK",
  "FST4": "MFSK",
  "FST4W": "MFSK",
  "FT4": "MFSK",
  "GTOR": "TOR",
  "HELL80": "HELL",
  "HELLX5": "HELL",
  "HELLX9": "HELL",
  "HFSK": "HELL",
  "ISCAT-A": "ISCAT",
  "ISCAT-B": "ISCAT",
  "JS8": "MFSK",
  "JT4A": "JT4",
  "JT4B": "JT4",
  "JT4C": "JT4",
  "JT4D": "JT4",
  "JT4E": "JT4",
  "JT4F": "JT4",
  "JT4G": "JT4",
  "JT65A": "JT65",
  "JT65B": "JT65",
  "JT65B2": "JT65",
  "JT65C": "JT65",
  "JT65C2": "JT65",
  "JT9-1": "JT9",
  "JT9-10": "JT9",
  "JT9-2": "JT9",
  "JT9-30": "JT9",
  "JT9-5": "JT9",
  "JT9A": "JT9",
  "JT9B": "JT9",
  "JT9C": "JT9",
  "JT9D": "JT9",
  "JT9E": "JT9",
  "JT9E FAST": "JT9",
  "JT9F": "JT9",
  "JT9F FAST": "JT9",
  "JT9G": "JT9",
  "JT9G FAST": "JT9",
  "JT9H": "JT9",
  "JT9H FAST": "JT9",
  "JTMS": "MFSK",
  "LSB": "SSB",
  "M17": "DIGITALVOICE",
  "MFSK11": "MFSK",
  "MFSK128": "MFSK",
  "MFSK128L": "MFSK",
  "MFSK16": "MFSK",
  "MFSK22": "MFSK",
  "MFSK31": "MFSK",
  "MFSK32": "MFSK",
  "MFSK4": "MFSK",
  "MFSK64": "MFSK",
  "MFSK64L": "MFSK",
  "MFSK8": "MFSK",
  "NAVTEX": "TOR",
  "OLIVIA 16/1000": "OLIVIA",
  "OLIVIA 16/500": "OLIVIA",
  "OLIVIA 32/1000": "OLIVIA",
  "OLIVIA 4/125": "OLIVIA",
  "OLIVIA 4/250": "OLIVIA",
  "OLIVIA 8/250": "OLIVIA",
  "OLIVIA 8/500": "OLIVIA",
  "OPERA-BEACON": "OPERA",
  "OPERA-QSO": "OPERA",
  "PAC2": "PAC",
  "PAC3": "PAC",
  "PAC4": "PAC",
  "PAX2": "PAX",
  "PCW": "CW",
  "PSK10": "PSK",
  "PSK1000": "PSK",
  "PSK1000C2": "PSK",
  "PSK1000R": "PSK",
  "PSK1000RC2": "PSK",
  "PSK125": "PSK",
  "PSK125C12": "PSK",
  "PSK125R": "PSK",
  "PSK125RC10": "PSK",
  "PSK125RC12": "PSK",
  "PSK125RC16": "PSK",
  "PSK125RC4": "PSK",
  "PSK125RC5": "PSK",
  "PSK250": "PSK",
  "PSK250C6": "PSK",
  "PSK250R": "PSK",
  "PSK250RC2": "PSK",
  "PSK250RC3": "PSK",
  "PSK250RC5": "PSK",
  "PSK250RC6": "PSK",
  "PSK250RC7": "PSK",
  "PSK31": "PSK",
  "PSK500": "PSK",
  "PSK500C2": "PSK",
  "PSK500C4": "PSK",
  "PSK500R": "PSK",
  "PSK500RC2": "PSK",
  "PSK500RC3": "PSK",
  "PSK500RC4": "PSK",
  "PSK63": "PSK",
  "PSK63F": "PSK",
  "PSK63RC10": "PSK",
  "PSK63RC20": "PSK",
  "PSK63RC32": "PSK",
  "PSK63RC4": "PSK",
  "PSK63RC5": "PSK",
  "PSK800C2": "PSK",
  "PSK800RC2": "PSK",
  "PSKAM10": "PSK",
  "PSKAM31": "PSK",
  "PSKAM50": "PSK",
  "PSKFEC31": "PSK",
  "PSKHELL": "HELL",
  "Q65": "MFSK",
  "QPSK125": "PSK",
  "QPSK250": "PSK",
  "QPSK31": "PSK",
  "QPSK500": "PSK",
  "QPSK63": "PSK",
  "QRA64A": "QRA64",
  "QRA64B": "QRA64",
  "QRA64C": "QRA64",
  "QRA64D": "QRA64",
  "QRA64E": "QRA64",
  "ROS-EME": "ROS",
  "ROS-HF": "ROS",
  "ROS-MF": "ROS",
  "SCAMP_FAST": "FSK",
  "SCAMP_OO": "MTONE",
  "SCAMP_OO_SLW": "MTONE",
  "SCAMP_SLOW": "FSK",
  "SCAMP_VSLOW": "FSK",
  "SIM31": "PSK",
  "SITORB": "TOR",
  "SLOWHELL": "HELL",
  "THOR-M": "THOR",
  "THOR100": "THOR",
  "THOR11": "THOR",
  "THOR16": "THOR",
  "THOR22": "THOR",
  "THOR25X4": "THOR",
  "THOR4": "THOR",
  "THOR5": "THOR",
  "THOR50X1": "THOR",
  "THOR50X2": "THOR",
  "THOR8": "THOR",
  "THRBX": "THRB",
  "THRBX1": "THRB",
  "THRBX2": "THRB",
  "THRBX4": "THRB",
  "THROB1": "THRB",
  "THROB2": "THRB",
  "THROB4": "THRB",
  "USB": "SSB",
  "VARA FM 1200": "DYNAMIC",
  "VARA FM 9600": "DYNAMIC",
  "VARA HF": "DYNAMIC",
  "VARA SATELLITE": "DYNAMIC",
});
